import { useQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  ADMIN_ADD_MANUAL_ORDER,
  AdminBrandsPicker,
  AdminDatePickerModal,
  AdminHeader,
  AdminLocationsPicker,
  AdminSelect,
  AdminSingleLocationPicker,
  AdminSpinner,
  Div,
  ErrorToast,
  ExportIcon,
  ExportReportModal,
  H5,
  HeaderIconWrapper,
  Icon,
  ListSpinner,
  NotificationAlertWrapper,
  PlusIcon,
  RefreshIcon,
  SearchInput,
  SimpleBrandEntity,
  SimpleLocationEntity,
  TooltipWrapper,
  createOrdersReportExport,
  fetchAndDownloadCsvFile,
  getBrandOwnerDashboardAdvancedQueryTypes,
  getOrdersReportExport,
  handleError,
  handleSetUrlSearchParams,
  isAdmin,
  isBrandOwner,
  isPartner,
  isProd,
  lightTheme,
  menuItems,
  queryKeys,
  read,
  renderAdvancedQueryTypesOptionLabel,
  renderDateLabelForFileName,
  renderDateLabelWithToday,
  setTimeStartEnd,
  setTimeStartEndMonth,
  store,
  useIsMobile,
  useOrders,
  useReportExportSocket,
  virtualizerStyle,
  MerchantBillingNotices,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import debouce from 'lodash.debounce';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AdvancedQueryTypes,
  LocationStatus,
  QueryStatus,
  SortByDate,
} from '../../types/shared.types';
import { OrderComponentV2 } from './OrderComponentV2';
import { OrderHistoryComponent } from './OrderHistoryComponent';
import { useOrdersSocket } from './OrdersSocket';
import { SearchOrderByIdModal } from './SearchOrderByIdModal';
import { OrdersTableHeader } from './components/OrdersTableHeader';
import { useOrdersInfiniteScroll } from './hooks/useOrdersInfiniteScroll';

const orderStatusOptions = [
  { name: 'Active', value: LocationStatus.ACTIVE },
  { name: 'Completed', value: LocationStatus.COMPLETED },
  { name: 'Canceled', value: LocationStatus.CANCELLED },
  { name: 'History', value: LocationStatus.HISTORY },
];

const typeOptions = (
  <optgroup label="Select Status">
    {orderStatusOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

const locationLSNode = 'vgrubs-admin-orders-location';

export const Orders = () => {
  const { setDataOrders, setDataHistory, hasNewOrder, setHasNewOrder } =
    useOrders((state) => state);

  const [selectedLocations, setSelectedLocations] = useState<
    SimpleLocationEntity[]
  >([]);
  const locationFromLS = JSON.parse(read(locationLSNode) as string);
  const [selectedLocation, setSelectedLocation] =
    useState<SimpleLocationEntity | null>(locationFromLS || null);
  const [selectedBrands, setSelectedBrands] = useState<SimpleBrandEntity[]>([]);
  const [searchModal, setSearchModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const today = new Date();
  const INITIAL_DATE = [
    setTimeStartEnd(today, 'start'),
    setTimeStartEnd(today, 'end'),
  ];
  const INITIAL_DATE_MONTH = [
    setTimeStartEndMonth(today, 'start'),
    setTimeStartEndMonth(today, 'today'),
  ];

  const [dateInput, setDateInput] = useState<Date[] | string[]>(
    INITIAL_DATE_MONTH
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get('status') ??
    orderStatusOptions[0].value) as LocationStatus;
  const from = searchParams.get('from') ?? INITIAL_DATE[0];
  const to = searchParams.get('to') ?? INITIAL_DATE[1];
  const locationIdsParam = searchParams.get('locationIds');
  const brandIdsParam = searchParams.get('brandIds');
  const initialQuery = searchParams.get('query') || '';

  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState<string | null>(initialQuery || null);

  const { data: advancedQueryTypesData } = useQuery({
    queryKey: queryKeys.brandOwner.advancedQueryTypes,
    queryFn: async () => {
      const { data } = await getBrandOwnerDashboardAdvancedQueryTypes();
      return data.data;
    },
    enabled: isBrandOwner,
  });
  const advancedQueryTypes = advancedQueryTypesData ?? [];
  const advancedQueryType =
    searchParams.get('type') ?? (advancedQueryTypes[0] as string | undefined);

  const filters = {
    query,
    from,
    to,
    status,
    selectedLocation,
    advancedQueryType,
    locationIdsParam,
    brandIdsParam,
    isAdmin,
    isBrandOwner,
    isPartner,
  };

  const {
    orders,
    dataHistory,
    observerRef,
    isFetchingNextPage,
    isFetching,
    status: queryStatus,
    refetch,
    saveScrollPosition,
  } = useOrdersInfiniteScroll(filters);

  useEffect(() => {
    setDataOrders(orders);
    setDataHistory(dataHistory);
  }, [orders, dataHistory, setDataOrders, setDataHistory]);

  const { ordersSocketEmit } = useOrdersSocket({
    from: '123',
    to,
    locationIds: selectedLocations,
    brandIds: selectedBrands,
    status,
  });

  const setAdvancedQueryType = (value: string) => {
    handleSetUrlSearchParams('type', value, setSearchParams);
  };

  const advancedQueryTypeOptions = (
    <>
      {advancedQueryTypes.map((r, i) => (
        <option value={r} key={i}>
          {renderAdvancedQueryTypesOptionLabel(r as AdvancedQueryTypes)}
        </option>
      ))}
    </>
  );

  const setDate = (date: string[] | Date[]) => {
    handleSetUrlSearchParams('from', date[0].toString(), setSearchParams);
    handleSetUrlSearchParams('to', date[1].toString(), setSearchParams);
  };

  const debouncedSearch = useMemo(
    () => debouce((value: string | null) => setQuery(value), 500),
    []
  );

  const handleLocationsLoaded = (loadedLocations: SimpleLocationEntity[]) => {
    const brandIds = brandIdsParam
      ? brandIdsParam
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !Number.isNaN(id))
      : [];
    if (brandIds.length === 0) return;

    const brandsFromLocations: SimpleBrandEntity[] = [];
    brandIds.forEach((brandId) => {
      for (const location of loadedLocations) {
        const brand = location.brands.find((b) => b.id === brandId);
        if (brand) {
          brandsFromLocations.push(brand);
          break;
        }
      }
    });
    setSelectedBrands(brandsFromLocations);
  };

  const handleDateApply = () => {
    if (dateInput.length < 2) {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[0], 'end'),
      ]);
      setDateInput([dateInput[0], dateInput[0]] as Date[]);
    } else {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[1], 'end'),
      ]);
    }
  };

  const handleLocationsSubmit = (
    _locationIds?: number[],
    locationObjects?: SimpleLocationEntity[]
  ) => {
    handleSetUrlSearchParams('brandIds', null, setSearchParams);
    setSelectedBrands([]);
    if (locationObjects) setSelectedLocations(locationObjects);
    ordersSocketEmit();
  };

  const handleLocationSubmit = (l: SimpleLocationEntity) => {
    setSelectedLocation(l);
    store(locationLSNode, JSON.stringify(l));
    ordersSocketEmit();
  };

  const handleBrandsSubmit = () => ordersSocketEmit();

  const ordersRefresh = () => {
    setHasNewOrder(false);
    refetch();
  };

  const exportLocations = isPartner
    ? selectedLocation
      ? [selectedLocation]
      : []
    : selectedLocations;

  const currentExportIdRef = useRef<number | null>(null);

  const { observe: observeReportExport } = useReportExportSocket(
    async (payload) => {
      if (currentExportIdRef.current !== payload.reportExportId) return;
      observeReportExport(payload.reportExportId, false);
      try {
        const { data } = await getOrdersReportExport(payload.reportExportId);
        const detail = data.data;
        if (payload.fail || detail.status === 'failed') {
          ErrorToast(detail.errorMessage || 'Export failed');
        } else if (detail.downloadUrl) {
          await fetchAndDownloadCsvFile(
            detail.downloadUrl,
            `Orders Report ${renderDateLabelForFileName(
              from.toString(),
              to.toString()
            )}.csv`
          );
        }
      } catch (error) {
        ErrorToast(handleError(error));
      } finally {
        currentExportIdRef.current = null;
        setExportLoading(false);
      }
    }
  );

  async function handleExportReport(includePhysicalLocationsOnly: boolean) {
    try {
      setExportLoading(true);
      const locationIds = selectedLocations.map((l) => l.id);
      const brandIds = selectedBrands.map((b) => b.id);
      const { data } = await createOrdersReportExport({
        from: DateTime.fromISO(from.toString()).toISODate() || '',
        to: DateTime.fromISO(to.toString()).toISODate() || '',
        locationId: isPartner ? selectedLocation?.id ?? undefined : undefined,
        locationIds:
          !isPartner && locationIds.length > 0 ? locationIds : undefined,
        brandIds:
          isAdmin && !includePhysicalLocationsOnly && brandIds.length > 0
            ? brandIds
            : undefined,
        ordersFind: status || undefined,
        query: query || undefined,
        includePhysicalLocationsOnly: includePhysicalLocationsOnly || undefined,
        sortByDate:
          status === LocationStatus.ACTIVE ? SortByDate.ASC : SortByDate.DESC,
      });
      currentExportIdRef.current = data.data.id;
      observeReportExport(data.data.id, true);
    } catch (error) {
      ErrorToast(handleError(error));
      setExportLoading(false);
    }
  }

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value || null);
    handleSetUrlSearchParams('query', value, setSearchParams);
  };

  const resetToToday = () => {
    setDate(INITIAL_DATE);
    setDateInput(INITIAL_DATE);
  };

  const resetToMonth = () => {
    setDate(INITIAL_DATE_MONTH);
    setDateInput(INITIAL_DATE_MONTH);
  };

  const isHistoryOrCancelled =
    status === LocationStatus.HISTORY || status === LocationStatus.CANCELLED;

  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    setScrollMargin(listRef.current?.offsetTop ?? 0);
  }, [orders.length, isHistoryOrCancelled]);

  const rowVirtualizer = useWindowVirtualizer({
    count: orders.length,
    estimateSize: () => (isMobile ? 204 : 74),
    overscan: 15,
    scrollMargin: scrollMargin,
  });

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [isMobile, rowVirtualizer]);

  useLayoutEffect(() => {
    if (orders.length === 0) return;
    const saved = sessionStorage.getItem('admin-orders-scroll');
    if (saved !== null) {
      const scrollY = parseInt(saved, 10);
      if (!Number.isNaN(scrollY)) {
        sessionStorage.removeItem('admin-orders-scroll');
        window.scrollTo({ top: scrollY });
      }
    }
  }, [orders.length]);

  return (
    <>
      <AdminHeader title="Orders">
        <Div $flex $alignCenter $gap="20px" $gapSM="10px">
          {!(isPartner && isProd) && (
            <TooltipWrapper tooltip="Export Report">
              <HeaderIconWrapper
                $opened={false}
                onClick={() => setExportModal(true)}
              >
                <Icon $size="24" color={lightTheme.primary}>
                  <ExportIcon />
                </Icon>
              </HeaderIconWrapper>
            </TooltipWrapper>
          )}

          <NotificationAlertWrapper $show={hasNewOrder}>
            <HeaderIconWrapper $opened={false} onClick={ordersRefresh}>
              <RefreshIcon />
            </HeaderIconWrapper>
          </NotificationAlertWrapper>

          {isHistoryOrCancelled && (
            <AdminDatePickerModal
              mode="range"
              value={dateInput}
              onChange={setDateInput}
              click={handleDateApply}
            />
          )}

          <SearchInput
            value={inputValue}
            maxWidth="350px"
            maxWidthSM="120px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>

      {/* The merchant's billing banners, as on the tablet; nothing in admin. */}
      <MerchantBillingNotices />

      <Div $mobile $flex $between $alignCenter $mt="32" $mtSM="12">
        <H5 $mb="0">{renderDateLabelWithToday(from, to)}</H5>
        {isAdmin && (
          <Link
            to={ADMIN_ADD_MANUAL_ORDER}
            style={{
              display: 'inline-flex',
              borderRadius: '50%',
            }}
          >
            <HeaderIconWrapper $opened={false} tabIndex={-1} role={undefined}>
              <PlusIcon />
            </HeaderIconWrapper>
          </Link>
        )}
      </Div>

      <Div
        $justifyEnd
        $flex
        $between
        $alignCenter
        $gap="32px"
        $gapSM="8px"
        $columnSM
        $m="32px 0"
        $mSM="20px 0"
        $mtSM="12"
      >
        {isPartner && (
          <Div $width="50%" $widthSM="100%">
            <AdminSingleLocationPicker
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              handleSubmit={handleLocationSubmit}
            />
          </Div>
        )}

        {isBrandOwner && (
          <Div $width="50%" $widthSM="100%">
            <AdminSelect
              $shadowDesign
              value={advancedQueryType}
              options={advancedQueryTypeOptions}
              onChange={({ target }: { target: { value: string } }) => {
                resetToToday();
                setAdvancedQueryType(target.value);
              }}
            />
          </Div>
        )}

        {isAdmin && (
          <>
            <Div $width="33%" $widthSM="100%">
              <AdminLocationsPicker
                selectedLocations={selectedLocations}
                setSelectedLocations={setSelectedLocations}
                handleSubmit={() => handleLocationsSubmit()}
                persistToUrl
                onLocationsLoaded={handleLocationsLoaded}
                setSearchParams={setSearchParams}
              />
            </Div>
            <Div $width="33%" $widthSM="100%">
              <AdminBrandsPicker
                selectedLocations={selectedLocations}
                selectedBrans={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                handleSubmit={handleBrandsSubmit}
                persistToUrl
                setSearchParams={setSearchParams}
              />
            </Div>
          </>
        )}

        <Div $width={isAdmin ? '33%' : '50%'} $widthSM="100%">
          <AdminSelect
            $shadowDesign
            value={status}
            options={typeOptions}
            onChange={({ target }: { target: { value: string } }) => {
              if (
                target.value === LocationStatus.ACTIVE ||
                target.value === LocationStatus.COMPLETED
              ) {
                resetToToday();
              } else if (
                target.value === LocationStatus.HISTORY ||
                target.value === LocationStatus.CANCELLED
              ) {
                resetToMonth();
              }
              handleSetUrlSearchParams('status', target.value, setSearchParams);
            }}
          />
        </Div>
      </Div>

      <Div
        $flex
        $alignCenter
        $between
        $columnSM
        $gap="32px"
        $gapSM="20px"
        $m="32px 0"
        $mSM="20px 0"
        $desktop
      >
        <Div>
          <H5 $mb="0">{renderDateLabelWithToday(from, to)}</H5>
        </Div>
        {isAdmin && (
          <Link
            to={ADMIN_ADD_MANUAL_ORDER}
            // Round the Link itself so the focus outline follows the
            // button's circular shape instead of painting a square
            // around the bounding box. Also tightens the outline to
            // hug the wrapper.
            style={{
              display: 'inline-flex',
              borderRadius: '50%',
            }}
          >
            {/*
              Wrapper opts out of being its own tab stop — the parent
              <Link> already provides keyboard activation. Without this,
              Tab would land on both, painting two focus rings stacked.
            */}
            <HeaderIconWrapper $opened={false} tabIndex={-1} role={undefined}>
              <PlusIcon />
            </HeaderIconWrapper>
          </Link>
        )}
      </Div>

      {orders.length > 0 && (
        <Div $desktop>
          <OrdersTableHeader />
        </Div>
      )}

      {isHistoryOrCancelled ? (
        <OrderHistoryComponent
          dataHistory={dataHistory}
          isStatusCancelled={status === LocationStatus.CANCELLED}
          onBeforeNavigate={saveScrollPosition}
        />
      ) : (
        <Div ref={listRef}>
          <Div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const data = orders[virtualRow.index];
              if (!data) return null;
              const virtualizerStyleValue = virtualizerStyle(
                virtualRow,
                rowVirtualizer
              );
              return (
                <Div key={data.id} style={virtualizerStyleValue as any}>
                  <OrderComponentV2
                    data={data}
                    index={virtualRow.index}
                    onBeforeNavigate={saveScrollPosition}
                  />
                </Div>
              );
            })}
          </Div>
        </Div>
      )}

      {orders.length === 0 &&
        queryStatus !== QueryStatus.PENDING &&
        !isFetching &&
        !isFetchingNextPage && (
          <NoDataComponent
            image={menuItems}
            title="No Orders"
            description={`You currently don’t have ${status} orders.`}
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />

      {((isFetching && !isFetchingNextPage) || exportLoading) && (
        <AdminSpinner />
      )}

      <SearchOrderByIdModal
        modalIsOpen={searchModal}
        closeModal={() => setSearchModal(false)}
      />

      <ExportReportModal
        isOpen={exportModal}
        closeModal={() => setExportModal(false)}
        onConfirm={handleExportReport}
        dateLabel={renderDateLabelWithToday(from, to)}
        selectedLocations={exportLocations}
        selectedBrands={isAdmin ? selectedBrands : []}
        statusLabel={
          orderStatusOptions.find((o) => o.value === status)?.name ?? null
        }
        query={query}
      />
    </>
  );
};
